/**
 * <IconBubble> — a glyph inside a tinted disc (atom).
 *
 * The decorative pastille the brand puts in front of an action or a result: quick-action
 * tiles, the green check of a confirmation. It is deliberately **not** pressable — an
 * `<IconButton>` inside a pressable tile would swallow the tile's own press — so the
 * surrounding `<Card>` / `<Pressable>` owns the interaction.
 *
 * No hex literals: disc and glyph colors are semantic tokens; the radius is half the size,
 * which is intrinsic geometry, not a design token.
 *
 * Dans un encart, un disque teinté perd son fond, le glyphe reste à sa place (`hooks/useOnInset`).
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { useOnInset } from "../../../hooks/useOnInset"
import { pillFillOn, type SemanticColorKey } from "../../../theme/tokens/colors"
import { Icon, type IconName } from "../Icon"

/** Le diamètre par défaut de la pastille — celui des tuiles de geste de l'accueil. */
export const BUBBLE_SIZE = 36

export type IconBubbleProps = {
    iconName: IconName
    /** Disc diameter in px — defaults to `BUBBLE_SIZE` (36). */
    size?: number
    /** Disc fill token — defaults to `primary-soft` (lavande). */
    color?: SemanticColorKey
    /** Glyph color token — defaults to `primary` (marine). */
    iconColor?: SemanticColorKey
    /** Glyph size in px — defaults to half the disc. */
    iconSize?: number
    testID?: string
}

export function IconBubble(props: IconBubbleProps) {
    const {
        iconName,
        size = BUBBLE_SIZE,
        color = "primary-soft",
        iconColor = "primary",
        iconSize = Math.round(size / 2),
        testID,
    } = props
    const fill = pillFillOn(color, useOnInset())

    return (
        <View testID={testID} style={styles.bubble(size, fill)}>
            <Icon name={iconName} size={iconSize} color={iconColor} />
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    bubble: (size: number, color: SemanticColorKey | null) => ({
        width: size,
        height: size,
        borderRadius: size / 2,
        alignItems: "center" as const,
        justifyContent: "center" as const,
        flexShrink: 0,
        backgroundColor: color != null ? theme.colors[color] : "transparent",
    }),
}))
